import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';

type CachedEntry = [contentId: string, lastSeenRealtimeAccessRequestCreationTimestamp: number];

export const MINUTE_LOOKBACK = 5;
const MINUTE_LOOKBACK_IN_MS = MINUTE_LOOKBACK * 60 * 1000;

export const isWithinLastFiveMinutes = (timestamp: number) => {
	return Date.now() - timestamp < MINUTE_LOOKBACK_IN_MS;
};

// The cached entity is a comma-separated list of <contentId>:<timestamp> string pairs
// Looks like this: "contentId1:1230498,contentId2:390489823,contentId3:3092480"
const getLastSeenRealtimeRequestsCache = (): CachedEntry[] => {
	try {
		const cachedContentTimestampPairs: string =
			localStorage.getItem(keys.LAST_SHOWN_REALTIME_REQUEST_CREATED_AT) ?? '';

		return cachedContentTimestampPairs
			?.split(',')
			.filter((entry) => entry)
			.map((entry) => entry.split(':'))
			.filter((entry) => entry[0] && entry[1])
			.map(([contentId, timestamp]) => [contentId, Number(timestamp)]);
	} catch (e) {
		// ToDo: return some error
		return [];
	}
};

const setLastSeenRealtimeRequestsCache = (entries: CachedEntry[]) => {
	try {
		const filteredEntries = entries
			.filter(
				(entry) =>
					entry[0] && entry[1] && typeof entry[0] === 'string' && typeof entry[1] === 'number',
			)
			.map((entry) => entry.join(':'));

		if (filteredEntries.length) {
			localStorage.setItem(keys.LAST_SHOWN_REALTIME_REQUEST_CREATED_AT, filteredEntries.join(','));
		} else {
			localStorage.removeItem(keys.LAST_SHOWN_REALTIME_REQUEST_CREATED_AT);
		}
	} catch (e) {
		// ToDo: Emit operational analytic event
	}
};

export const getLastSeenRealtimeRequestCreationTimestamp = (contentId: string): number => {
	const cachedContentTimestampPairs = getLastSeenRealtimeRequestsCache();
	const cachedEntry = cachedContentTimestampPairs.find(([cachedId]) => cachedId === contentId);
	const timestamp = cachedEntry?.[1] ?? 0;
	return timestamp;
};

// If new timestamp is < 5 mins old Then replace any existing cache entry for this contentId or create a new one
// else IF old timestamp is > 5 mins old Then remove it from cache
export const updateLastSeenRealtimeRequestCache = (contentId: string, newTimestamp: number) => {
	const entries = getLastSeenRealtimeRequestsCache();
	const currentCachedContentTimestampPairIndex = entries.findIndex(
		(entry) => entry[0] === contentId,
	);

	// If there's a new request that's less than 5 mins old, cache it
	if (isWithinLastFiveMinutes(newTimestamp)) {
		const updatedEntry: CachedEntry = [contentId, newTimestamp];

		if (currentCachedContentTimestampPairIndex === -1) {
			entries.push(updatedEntry);
		} else {
			entries[currentCachedContentTimestampPairIndex] = updatedEntry;
		}
	}

	setLastSeenRealtimeRequestsCache(entries);
};

export const makeUpdateLastSeenRealtimeRequestCacheCallback =
	(contentId: string, newTimestamp: number) => () =>
		updateLastSeenRealtimeRequestCache(contentId, newTimestamp);

export const isMoreRecentThanCachedRequestTimestamp = (
	contentId: string,
	requestTimestamp: number,
) => {
	return (
		requestTimestamp > getLastSeenRealtimeRequestCreationTimestamp(contentId) &&
		isWithinLastFiveMinutes(requestTimestamp)
	);
};
